/**
 * UserEdit Page - Edit an existing user
 *
 * Requirements: 2.3
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { AppHeader } from '@shared/components';
import { usePermissions } from '@shared/auth';
import { UserForm } from '../components';
import { getUser, updateUser } from '../api';
import type { ManagedUser, CreateUserRequest } from '../api';
import { useAdminContext } from '../AdminPage';
import styles from './UserEdit.module.css';

export const UserEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { getAccessToken } = usePermissions();
  const { refreshUserList } = useAdminContext();

  const [user, setUser] = useState<ManagedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;

      setIsLoading(true);
      setLoadError(null);

      try {
        const token = await getAccessToken();
        const result = await getUser(token, userId);

        if (result.success) {
          setUser(result.data);
        } else {
          setLoadError(result.error.message);
        }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load user');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [userId, getAccessToken]);

  const handleSubmit = useCallback(
    async (data: CreateUserRequest) => {
      if (!userId) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const token = await getAccessToken();
        const result = await updateUser(token, userId, {
          name: data.name,
          roles: data.roles
        });

        if (result.success) {
          refreshUserList();
          navigate('/admin');
        } else {
          setSubmitError(result.error.message);
        }
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to update user');
      } finally {
        setIsSubmitting(false);
      }
    },
    [userId, getAccessToken, navigate]
  );

  return (
    <div className={styles.container}>
      <AppHeader currentApp="admin" />
      <main className={styles.main}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.large} label="Loading user..." />
          </div>
        ) : loadError ? (
          <div className={styles.errorContainer}>
            <h2 className={styles.errorTitle}>Error Loading User</h2>
            <p className={styles.errorMessage}>{loadError}</p>
            <button
              className={styles.backButton}
              onClick={() => navigate('/admin')}
            >
              Back to User List
            </button>
          </div>
        ) : user ? (
          <>
            <div className={styles.headerSection}>
              <h1 className={styles.title}>Edit User</h1>
              <p className={styles.subtitle}>
                Update roles for {user.name}
              </p>
            </div>
            <UserForm
              mode="edit"
              initialData={user}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              error={submitError}
            />
          </>
        ) : null}
      </main>
    </div>
  );
};
